/**
 * MEVIA - Action Plan Detail View
 * Detailed view of an action plan with editing, tracking, subtasks and budget capabilities
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  ArrowLeft,
  User,
  Users,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  MessageSquare,
  Send,
  Download,
  Pencil,
  Save,
  X,
  RefreshCw,
  Target,
  BookOpen,
  History,
  ChevronRight,
  Play,
  RotateCcw,
  Sparkles,
  DollarSign,
  Plus,
  Trash2,
  CheckSquare,
  Square,
  Upload,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { AssigneesPanel, ApprovalPanel, EvidencePanel, HistoryPanel } from './ActionPlanCollaboration';
import html2canvas from 'html2canvas';
import logger from '../utils/logger';

// Status configuration
const STATUS_CONFIG = {
  draft: { es: 'Borrador', en: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  active: { es: 'Activo', en: 'Active', color: 'bg-blue-100 text-blue-800', icon: Clock },
  pending_validation: { es: 'Por Validar', en: 'Pending Validation', color: 'bg-amber-100 text-amber-800', icon: AlertCircle },
  validated: { es: 'Validado', en: 'Validated', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { es: 'Cerrado', en: 'Closed', color: 'bg-purple-100 text-purple-800', icon: CheckCircle }
};

const PRIORITY_CONFIG = {
  high: { es: 'Alta', en: 'High', color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { es: 'Media', en: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { es: 'Baja', en: 'Low', color: 'bg-green-100 text-green-700 border-green-200' }
};

const PROGRESS_STATUS = {
  not_started: { es: 'Sin Iniciar', en: 'Not Started', color: 'text-gray-500' },
  in_progress: { es: 'En Progreso', en: 'In Progress', color: 'text-blue-600' },
  completed: { es: 'Completado', en: 'Completed', color: 'text-amber-600' },
  validated: { es: 'Validado', en: 'Validated', color: 'text-green-600' }
};

const ActionPlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { hasAccess } = useSubscription();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [editMode, setEditMode] = useState(searchParams.get('edit') === 'true');
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [showValidateDialog, setShowValidateDialog] = useState(searchParams.get('validate') === 'true');
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // New state for subtasks and budget
  const [newSubtaskTitle, setNewSubtaskTitle] = useState({});
  const [showBudgetDialog, setShowBudgetDialog] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');

  // Translations
  const t = {
    back: language === 'es' ? 'Volver' : 'Back',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    notFound: language === 'es' ? 'Plan no encontrado' : 'Plan not found',
    planType: {
      individual: language === 'es' ? 'Plan Individual' : 'Individual Plan',
      company: language === 'es' ? 'Plan Empresarial' : 'Company Plan'
    },
    employee: language === 'es' ? 'Empleado' : 'Employee',
    department: language === 'es' ? 'Departamento' : 'Department',
    createdAt: language === 'es' ? 'Creado' : 'Created',
    createdBy: language === 'es' ? 'Creado por' : 'Created by',
    status: language === 'es' ? 'Estado' : 'Status',
    progress: language === 'es' ? 'Progreso General' : 'Overall Progress',
    tasks: language === 'es' ? 'tareas' : 'tasks',
    subtasks: language === 'es' ? 'subtareas' : 'subtasks',
    completed: language === 'es' ? 'completadas' : 'completed',
    developmentAreas: language === 'es' ? 'Áreas de Desarrollo' : 'Development Areas',
    objective: language === 'es' ? 'Objetivo' : 'Objective',
    actions: language === 'es' ? 'Acciones' : 'Actions',
    resources: language === 'es' ? 'Recursos' : 'Resources',
    deadline: language === 'es' ? 'Fecha Límite' : 'Deadline',
    priority: language === 'es' ? 'Prioridad' : 'Priority',
    itemProgress: language === 'es' ? 'Progreso' : 'Progress',
    adminNotes: language === 'es' ? 'Notas del Administrador' : 'Admin Notes',
    employeeNotes: language === 'es' ? 'Notas del Empleado' : 'Employee Notes',
    comments: language === 'es' ? 'Comentarios' : 'Comments',
    addComment: language === 'es' ? 'Agregar comentario...' : 'Add a comment...',
    send: language === 'es' ? 'Enviar' : 'Send',
    edit: language === 'es' ? 'Editar' : 'Edit',
    save: language === 'es' ? 'Guardar' : 'Save',
    cancel: language === 'es' ? 'Cancelar' : 'Cancel',
    saving: language === 'es' ? 'Guardando...' : 'Saving...',
    activate: language === 'es' ? 'Activar Plan' : 'Activate Plan',
    activateDesc: language === 'es' 
      ? 'Al activar el plan, el empleado podrá verlo y registrar su progreso. ¿Deseas continuar?'
      : 'By activating the plan, the employee will be able to view it and track their progress. Do you want to continue?',
    validate: language === 'es' ? 'Validar Plan' : 'Validate Plan',
    validateDesc: language === 'es'
      ? 'Al validar el plan, confirmas que todas las tareas completadas han sido revisadas. ¿Deseas continuar?'
      : 'By validating the plan, you confirm that all completed tasks have been reviewed. Do you want to continue?',
    confirm: language === 'es' ? 'Confirmar' : 'Confirm',
    exportPdf: language === 'es' ? 'Exportar PDF' : 'Export PDF',
    exporting: language === 'es' ? 'Exportando...' : 'Exporting...',
    restore: language === 'es' ? 'Restaurar Original' : 'Restore Original',
    restoreDesc: language === 'es'
      ? 'Esto restaurará el plan a su versión original generada automáticamente. Solo disponible si no hay progreso registrado.'
      : 'This will restore the plan to its original auto-generated version. Only available if no progress has been recorded.',
    dataSources: language === 'es' ? 'Fuentes de Datos' : 'Data Sources',
    indicators: language === 'es' ? 'Indicadores HR' : 'HR Indicators',
    enneagram: language === 'es' ? 'Eneagrama' : 'Enneagram',
    type: language === 'es' ? 'Tipo' : 'Type',
    history: language === 'es' ? 'Historial' : 'History',
    noComments: language === 'es' ? 'Sin comentarios aún' : 'No comments yet',
    updateSuccess: language === 'es' ? 'Plan actualizado' : 'Plan updated',
    commentAdded: language === 'es' ? 'Comentario agregado' : 'Comment added',
    planActivated: language === 'es' ? 'Plan activado' : 'Plan activated',
    planValidated: language === 'es' ? 'Plan validado' : 'Plan validated',
    error: language === 'es' ? 'Error al procesar la solicitud' : 'Error processing request',
    // New translations for subtasks and budget
    subtaskList: language === 'es' ? 'Checklist de Subtareas' : 'Subtask Checklist',
    addSubtask: language === 'es' ? 'Añadir subtarea...' : 'Add subtask...',
    requiresBudget: language === 'es' ? 'Requiere Presupuesto' : 'Requires Budget',
    noBudget: language === 'es' ? 'Sin presupuesto' : 'No budget',
    estimatedBudget: language === 'es' ? 'Presupuesto Estimado' : 'Estimated Budget',
    setBudget: language === 'es' ? 'Configurar Presupuesto' : 'Set Budget',
    budgetAmount: language === 'es' ? 'Monto' : 'Amount',
    subtaskAdded: language === 'es' ? 'Subtarea añadida' : 'Subtask added',
    subtaskUpdated: language === 'es' ? 'Subtarea actualizada' : 'Subtask updated',
    subtaskDeleted: language === 'es' ? 'Subtarea eliminada' : 'Subtask deleted',
    budgetUpdated: language === 'es' ? 'Presupuesto actualizado' : 'Budget updated'
  };

  const loadPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/action-plans/${planId}`);
      setPlan(response.data);
    } catch (error) {
      logger.error('Error loading plan:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  }, [planId, t.error]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const isAdmin = user?.rol === 'company_admin' || user?.rol === 'super_admin';
  const isOwner = plan?.employee_id === user?.id;
  const canEdit = isAdmin || isOwner;

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`/action-plans/${planId}`, {
        title: plan.title,
        description: plan.description
      });
      toast.success(t.updateSuccess);
      setEditMode(false);
      loadPlan();
    } catch (error) {
      logger.error('Error saving plan:', error);
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItemProgress = async (itemId, newStatus) => {
    try {
      await axios.put(`/action-plans/${planId}/items/${itemId}`, {
        progress_status: newStatus
      });
      toast.success(t.updateSuccess);
      loadPlan();
    } catch (error) {
      logger.error('Error updating item:', error);
      toast.error(t.error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setAddingComment(true);
      await axios.post(`/action-plans/${planId}/comments`, {
        content: newComment
      });
      setNewComment('');
      toast.success(t.commentAdded);
      loadPlan();
    } catch (error) {
      logger.error('Error adding comment:', error);
      toast.error(t.error);
    } finally {
      setAddingComment(false);
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`/action-plans/${planId}/activate`);
      toast.success(t.planActivated);
      setShowActivateDialog(false);
      loadPlan();
    } catch (error) {
      logger.error('Error activating plan:', error);
      toast.error(t.error);
    }
  };

  const handleValidate = async () => {
    try {
      await axios.put(`/action-plans/${planId}`, { status: 'validated' });
      toast.success(t.planValidated);
      setShowValidateDialog(false);
      loadPlan();
    } catch (error) {
      logger.error('Error validating plan:', error);
      toast.error(t.error);
    }
  };

  // Subtask handlers
  const handleToggleSubtask = async (itemId, subtaskId, currentValue) => {
    try {
      await axios.put(`/action-plans/${planId}/items/${itemId}/subtasks/${subtaskId}`, {
        is_completed: !currentValue
      });
      toast.success(t.subtaskUpdated);
      loadPlan();
    } catch (error) {
      logger.error('Error updating subtask:', error);
      toast.error(t.error);
    }
  };

  const handleAddSubtask = async (itemId) => {
    const title = newSubtaskTitle[itemId];
    if (!title?.trim()) return;
    
    try {
      await axios.post(`/action-plans/${planId}/items/${itemId}/subtasks`, {
        title: title.trim()
      });
      setNewSubtaskTitle({ ...newSubtaskTitle, [itemId]: '' });
      toast.success(t.subtaskAdded);
      loadPlan();
    } catch (error) {
      logger.error('Error adding subtask:', error);
      toast.error(t.error);
    }
  };

  const handleDeleteSubtask = async (itemId, subtaskId) => {
    try {
      await axios.delete(`/action-plans/${planId}/items/${itemId}/subtasks/${subtaskId}`);
      toast.success(t.subtaskDeleted);
      loadPlan();
    } catch (error) {
      logger.error('Error deleting subtask:', error);
      toast.error(t.error);
    }
  };

  // Budget handler
  const handleUpdateBudget = async () => {
    if (!showBudgetDialog) return;
    
    try {
      const amount = budgetAmount ? parseFloat(budgetAmount) : null;
      await axios.put(
        `/action-plans/${planId}/items/${showBudgetDialog}/budget?requires_budget=${amount !== null}&estimated_budget=${amount || 0}`
      );
      toast.success(t.budgetUpdated);
      setShowBudgetDialog(null);
      setBudgetAmount('');
      loadPlan();
    } catch (error) {
      logger.error('Error updating budget:', error);
      toast.error(t.error);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const element = document.getElementById('plan-content');
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`plan-${plan.employee_name || 'empresa'}-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success(language === 'es' ? 'PDF exportado' : 'PDF exported');
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      toast.error(t.error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat(language === 'es' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t.notFound}</p>
            <Button className="mt-4" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[plan.status]?.icon || FileText;

  // Calculate total budget
  const totalBudget = plan.items?.reduce((sum, item) => {
    return sum + (item.requires_budget && item.estimated_budget ? item.estimated_budget : 0);
  }, 0) || 0;

  const itemsWithBudget = plan.items?.filter(item => item.requires_budget).length || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.back}
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{plan.title}</h1>
                <p className="text-sm text-gray-500">{plan.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={handleExportPDF} disabled={exporting}>
                {exporting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exporting ? t.exporting : t.exportPdf}
              </Button>
              {isAdmin && plan.status === 'draft' && (
                <Button onClick={() => setShowActivateDialog(true)} className="bg-green-600 hover:bg-green-700">
                  <Play className="h-4 w-4 mr-2" />
                  {t.activate}
                </Button>
              )}
              {isAdmin && plan.status === 'pending_validation' && (
                <Button onClick={() => setShowValidateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t.validate}
                </Button>
              )}
              {isAdmin && plan.status === 'draft' && !editMode && (
                <Button onClick={() => setEditMode(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t.edit}
                </Button>
              )}
              {editMode && (
                <>
                  <Button variant="outline" onClick={() => setEditMode(false)}>
                    <X className="h-4 w-4 mr-2" />
                    {t.cancel}
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                    {saving ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {saving ? t.saving : t.save}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div id="plan-content" className="space-y-6">
          {/* Plan Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-teal-600" />
                    {t.planType[plan.plan_type]}
                  </CardTitle>
                  <Badge className={`${STATUS_CONFIG[plan.status]?.color} flex items-center gap-1`}>
                    <StatusIcon className="h-3 w-3" />
                    {STATUS_CONFIG[plan.status]?.[language]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {plan.employee_name && (
                    <div>
                      <p className="text-sm text-gray-500">{t.employee}</p>
                      <p className="font-medium">{plan.employee_name}</p>
                    </div>
                  )}
                  {plan.employee_email && (
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-sm">{plan.employee_email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">{t.createdAt}</p>
                    <p className="font-medium">{formatDate(plan.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.createdBy}</p>
                    <p className="font-medium">{plan.created_by_name || '-'}</p>
                  </div>
                </div>
                
                {/* Progress - now includes subtasks */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{t.progress}</span>
                    <span className="text-lg font-bold text-teal-600">{plan.progress?.percentage || 0}%</span>
                  </div>
                  <Progress value={plan.progress?.percentage || 0} className="h-3 mb-2" />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>
                      {plan.progress?.completed_subtasks || 0}/{plan.progress?.total_subtasks || 0} {t.subtasks} {t.completed}
                    </span>
                    <span>
                      {plan.progress?.completed_items || 0}/{plan.progress?.total_items || 0} {t.tasks}
                    </span>
                  </div>
                </div>

                {/* Budget Summary */}
                {itemsWithBudget > 0 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <DollarSign className="h-5 w-5" />
                      <span className="font-medium">{t.estimatedBudget}:</span>
                      <span className="text-lg font-bold">{formatCurrency(totalBudget)}</span>
                      <span className="text-sm">({itemsWithBudget} {language === 'es' ? 'items' : 'items'})</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column - Data Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  {t.dataSources}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* IPS Display - Prominent if available */}
                {plan.source_indicators?.ips && (
                  <div 
                    className="p-4 rounded-lg border-2"
                    style={{ 
                      backgroundColor: `${plan.source_indicators.ips.color}15`,
                      borderColor: plan.source_indicators.ips.color 
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-700">
                        {language === 'es' ? 'Índice de Permanencia Saludable' : 'Healthy Retention Index'}
                      </p>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span 
                        className="text-3xl font-bold"
                        style={{ color: plan.source_indicators.ips.color }}
                      >
                        {plan.source_indicators.ips.score?.toFixed(1)}
                      </span>
                      <span 
                        className="text-sm font-semibold px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: `${plan.source_indicators.ips.color}20`,
                          color: plan.source_indicators.ips.color
                        }}
                      >
                        {plan.source_indicators.ips.level?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {plan.source_indicators.ips.level === 'CRITICO' && (
                      <p className="text-sm mt-2 text-red-600">
                        ⚠️ {language === 'es' ? 'Requiere intervención inmediata' : 'Requires immediate intervention'}
                      </p>
                    )}
                    {plan.source_indicators.ips.level === 'ALTO_RIESGO' && (
                      <p className="text-sm mt-2 text-orange-600">
                        🔶 {language === 'es' ? 'Plan de retención prioritario' : 'Priority retention plan'}
                      </p>
                    )}
                  </div>
                )}
                {plan.source_indicators?.hr_indicators && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-700 mb-2">{t.indicators}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(plan.source_indicators.hr_indicators).slice(0, 6).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-600">{key}:</span>
                          <span className="font-medium">{typeof value === 'number' ? value.toFixed(0) : value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {plan.source_indicators?.enneagram && (
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="font-medium text-purple-700 mb-2">{t.enneagram}</p>
                    <p className="text-sm">
                      {t.type} {plan.source_indicators.enneagram.primary_type}
                      {plan.source_indicators.enneagram.wing_type && ` w${plan.source_indicators.enneagram.wing_type}`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Development Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-teal-600" />
                {t.developmentAreas}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-4">
                {plan.items?.map((item, index) => {
                  const completedSubtasks = item.subtasks?.filter(st => st.is_completed).length || 0;
                  const totalSubtasks = item.subtasks?.length || 0;
                  const subtaskProgress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks * 100) : 0;
                  
                  return (
                    <AccordionItem 
                      key={item.id} 
                      value={item.id}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center gap-4 flex-1 text-left">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            item.progress_status === 'validated' ? 'bg-green-100 text-green-700' :
                            item.progress_status === 'completed' ? 'bg-amber-100 text-amber-700' :
                            item.progress_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold truncate">{item.area}</p>
                              {item.requires_budget && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  {formatCurrency(item.estimated_budget)}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">{item.objective}</p>
                            {totalSubtasks > 0 && (
                              <div className="flex items-center gap-2 mt-1">
                                <Progress value={subtaskProgress} className="h-1.5 flex-1 max-w-24" />
                                <span className="text-xs text-gray-500">{completedSubtasks}/{totalSubtasks}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mr-4">
                            <Badge className={`${PRIORITY_CONFIG[item.priority]?.color} border`}>
                              {PRIORITY_CONFIG[item.priority]?.[language]}
                            </Badge>
                            <span className={`text-sm font-medium ${PROGRESS_STATUS[item.progress_status]?.color}`}>
                              {PROGRESS_STATUS[item.progress_status]?.[language]}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="space-y-4 pt-2">
                          {/* Actions */}
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">{t.actions}:</p>
                            <ul className="space-y-1">
                              {item.actions?.map((action, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                  <span>{typeof action === 'string' ? action : action?.description || JSON.stringify(action)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Subtasks Checklist */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                              <CheckSquare className="h-4 w-4 text-teal-600" />
                              {t.subtaskList} ({completedSubtasks}/{totalSubtasks})
                            </p>
                            <div className="space-y-2">
                              {item.subtasks?.map((subtask) => (
                                <div key={subtask.id} className="flex items-center gap-3 group">
                                  <Checkbox
                                    id={subtask.id}
                                    checked={subtask.is_completed}
                                    onCheckedChange={() => handleToggleSubtask(item.id, subtask.id, subtask.is_completed)}
                                    disabled={plan.status === 'draft' || plan.status === 'validated' || plan.status === 'closed'}
                                    className="data-[state=checked]:bg-teal-600"
                                  />
                                  <label 
                                    htmlFor={subtask.id}
                                    className={`flex-1 text-sm cursor-pointer ${subtask.is_completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                  >
                                    {subtask.title}
                                  </label>
                                  {isAdmin && plan.status !== 'validated' && plan.status !== 'closed' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                                      onClick={() => handleDeleteSubtask(item.id, subtask.id)}
                                    >
                                      <Trash2 className="h-3 w-3 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              
                              {/* Add new subtask (admin only) */}
                              {isAdmin && plan.status !== 'validated' && plan.status !== 'closed' && (
                                <div className="flex items-center gap-2 pt-2">
                                  <Plus className="h-4 w-4 text-gray-400" />
                                  <Input
                                    placeholder={t.addSubtask}
                                    value={newSubtaskTitle[item.id] || ''}
                                    onChange={(e) => setNewSubtaskTitle({ ...newSubtaskTitle, [item.id]: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && newSubtaskTitle[item.id]?.trim()) {
                                        handleAddSubtask(item.id);
                                      }
                                    }}
                                    className="flex-1 h-8 text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleAddSubtask(item.id)}
                                    disabled={!newSubtaskTitle[item.id]?.trim()}
                                    className="h-8"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Resources */}
                          {item.resources?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">{t.resources}:</p>
                              <div className="flex flex-wrap gap-2">
                                {item.resources.map((resource, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {resource}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Budget (admin only) */}
                          {isAdmin && (
                            <div className="flex items-center gap-4 pt-2 border-t">
                              <span className="text-sm font-medium text-gray-700">{t.requiresBudget}:</span>
                              {item.requires_budget ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  {formatCurrency(item.estimated_budget)}
                                </Badge>
                              ) : (
                                <span className="text-sm text-gray-500">{t.noBudget}</span>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setShowBudgetDialog(item.id);
                                  setBudgetAmount(item.estimated_budget?.toString() || '');
                                }}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                {t.setBudget}
                              </Button>
                            </div>
                          )}
                          
                          {/* Progress Update (for employees and admins) */}
                          {(plan.status === 'active' || plan.status === 'pending_validation') && (
                            <div className="flex items-center gap-4 pt-4 border-t">
                              <span className="text-sm font-medium">{t.itemProgress}:</span>
                              <Select
                                value={item.progress_status}
                                onValueChange={(value) => handleUpdateItemProgress(item.id, value)}
                                disabled={item.progress_status === 'validated'}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="not_started">{PROGRESS_STATUS.not_started[language]}</SelectItem>
                                  <SelectItem value="in_progress">{PROGRESS_STATUS.in_progress[language]}</SelectItem>
                                  <SelectItem value="completed">{PROGRESS_STATUS.completed[language]}</SelectItem>
                                  {isAdmin && (
                                    <SelectItem value="validated">{PROGRESS_STATUS.validated[language]}</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Assignees Panel - MEVIA v4.0 Phase 5 */}
                          <div className="pt-4 border-t">
                            <AssigneesPanel 
                              itemId={item.id}
                              planId={planId}
                              onUpdate={loadPlan}
                            />
                          </div>

                          {/* Item Evidence */}
                          <div className="pt-4 border-t">
                            <EvidencePanel 
                              planId={planId}
                              itemId={item.id}
                              onUpdate={loadPlan}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-teal-600" />
                {t.comments} ({plan.comments?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Comment */}
              <div className="flex gap-2 mb-6">
                <Textarea
                  placeholder={t.addComment}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1 min-h-[80px]"
                />
                <Button 
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || addingComment}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {addingComment ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {/* Comments List */}
              {plan.comments?.length > 0 ? (
                <div className="space-y-4">
                  {plan.comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-sm">{comment.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_role === 'company_admin' ? 'Admin' : 
                             comment.author_role === 'employee' ? (language === 'es' ? 'Empleado' : 'Employee') : 
                             comment.author_role}
                          </Badge>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">{t.noComments}</p>
              )}
            </CardContent>
          </Card>

          {/* Collaboration Section - MEVIA v4.0 Phase 5 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Approvals Panel */}
            <ApprovalPanel 
              planId={planId}
              planStatus={plan.status}
              onUpdate={loadPlan}
            />
            
            {/* Evidence Panel */}
            <EvidencePanel 
              planId={planId}
              onUpdate={loadPlan}
            />
          </div>

          {/* Activity History */}
          <HistoryPanel 
            planId={planId}
          />
        </div>
      </main>

      {/* Activate Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.activate}</DialogTitle>
            <DialogDescription>{t.activateDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleActivate} className="bg-green-600 hover:bg-green-700">
              {t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validate Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.validate}</DialogTitle>
            <DialogDescription>{t.validateDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidateDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleValidate} className="bg-blue-600 hover:bg-blue-700">
              {t.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Dialog */}
      <Dialog open={!!showBudgetDialog} onOpenChange={() => setShowBudgetDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.setBudget}</DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Define si este item requiere presupuesto y el monto estimado.' 
                : 'Define if this item requires budget and the estimated amount.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">{t.budgetAmount} (USD)</label>
            <Input
              type="number"
              placeholder="0.00"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-2">
              {language === 'es' 
                ? 'Deja vacío o 0 para marcar como "sin presupuesto"' 
                : 'Leave empty or 0 to mark as "no budget"'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetDialog(null)}>
              {t.cancel}
            </Button>
            <Button onClick={handleUpdateBudget} className="bg-teal-600 hover:bg-teal-700">
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionPlanDetail;
